import { useQuery } from '@tanstack/react-query';
import { getEnvVar } from '../lib/env';
import { getSupabaseClient } from '../lib/supabase';

interface MaterialUrlResponse {
  url: string;
  type: string;
  expires?: number;
}

/**
 * Hook to fetch a signed URL for protected materials (PDFs, images, etc.)
 * Thumbnails are public and don't need signed URLs.
 * Videos should use the Stream player with /api/stream/url.
 */
export function useMaterialUrl(materialId: string, materialType: string) {
  const supabase = getSupabaseClient();

  return useQuery({
    queryKey: ['material-url', materialId],
    queryFn: async (): Promise<MaterialUrlResponse> => {
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;

      if (!accessToken) {
        throw new Error('Not authenticated');
      }

      const apiUrl = getEnvVar('VITE_API_URL');
      const response = await fetch(
        `${apiUrl}/api/material/url?materialId=${materialId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to fetch URL' }));
        throw new Error(error.error || 'Failed to fetch material URL');
      }

      return response.json();
    },
    enabled: !!materialId && materialType !== 'video', // Videos use Stream player
    staleTime: 30 * 60 * 1000, // Cache for 30 minutes (URLs expire in 1 hour)
    retry: 1,
  });
}

interface VideoUrlResponse {
  provider: 'cloudflare' | 'mux';
  // Cloudflare Stream fields
  videoId?: string;
  signedToken?: string;
  iframeUrl?: string;
  // Mux fields
  playbackId?: string;
  thumbnailUrl?: string;
  // Common
  streamUrl?: string;
}

/**
 * Hook to fetch signed video URL (supports Cloudflare Stream and Mux)
 */
export function useVideoUrl(materialId: string, materialType: string) {
  const supabase = getSupabaseClient();

  return useQuery({
    queryKey: ['video-url', materialId],
    queryFn: async () => {
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;

      if (!accessToken) {
        throw new Error('Not authenticated');
      }

      const apiUrl = getEnvVar('VITE_API_URL');
      const response = await fetch(
        `${apiUrl}/api/stream/url?materialId=${materialId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to fetch URL' }));
        throw new Error(error.error || 'Failed to fetch video URL');
      }

      return response.json() as Promise<VideoUrlResponse>;
    },
    enabled: !!materialId && materialType === 'video',
    staleTime: 30 * 60 * 1000, // Cache for 30 minutes
    retry: 1,
  });
}

